import { useEffect, useRef } from 'react';
import { ChevronUp, ChevronDown, Lasso, Pentagon, Wand2, Crop } from 'lucide-react';
import type {
  EditorTool,
  BrushSettings,
  TextSettings,
  ShapeSettings,
  MarqueeSettings,
  MarqueeShape,
  FilterSettings,
  ShapeType,
} from '../types';
import { hexToRgb, rgbToHex, rgbToHsv, rgbToHsl, hsvToRgb } from '../lib/colors';

export type PropertiesPanelPlacement = 'right' | 'bottom';

interface PropertiesPanelProps {
  isOpen: boolean;
  placement: PropertiesPanelPlacement;
  activeTool: EditorTool;
  brushSettings: BrushSettings;
  textSettings: TextSettings;
  shapeSettings: ShapeSettings;
  marqueeSettings: MarqueeSettings;
  filterSettings: FilterSettings;
  /** Hex color under the cursor — used by the eyedropper panel. */
  colorUnderCursor: string;
  /** Cursor scene-space coordinates — used by the eyedropper panel. */
  cursorX: number;
  cursorY: number;
  /** The user's currently-selected (brush/fill/foreground) color. */
  selectedColor: string;
  /** Fired when the user picks a color via the eyedropper's color wheel. */
  onColorPicked: (color: string) => void;
  onUpdateBrush: (settings: Partial<BrushSettings>) => void;
  onUpdateText: (settings: Partial<TextSettings>) => void;
  onUpdateShape: (settings: Partial<ShapeSettings>) => void;
  onUpdateMarquee: (settings: Partial<MarqueeSettings>) => void;
  onUpdateFilter: (settings: Partial<FilterSettings>) => void;
  onApplyCrop: () => void;
  onToggle: () => void;
}

export function PropertiesPanel({
  isOpen,
  placement,
  activeTool,
  brushSettings,
  textSettings,
  shapeSettings,
  marqueeSettings,
  filterSettings,
  colorUnderCursor,
  cursorX,
  cursorY,
  selectedColor,
  onColorPicked,
  onUpdateBrush,
  onUpdateText,
  onUpdateShape,
  onUpdateMarquee,
  onUpdateFilter,
  onApplyCrop,
  onToggle,
}: PropertiesPanelProps) {
  const isRight = placement === 'right';

  if (!isOpen) {
    if (isRight) {
      return (
        <button
          type="button"
          className="w-full h-10 bg-background border-l border-border flex items-center justify-between px-3 text-muted-foreground hover:text-foreground shrink-0"
          onClick={onToggle}
          title="Show properties"
        >
          <span className="text-xs font-medium">Properties</span>
          <ChevronDown className="h-4 w-4" />
        </button>
      );
    }
    return (
      <button
        type="button"
        className="w-full h-10 bg-background border-t border-border flex items-center justify-between px-3 text-muted-foreground hover:text-foreground"
        onClick={onToggle}
        title="Show properties"
      >
        <span className="text-xs font-medium">Properties</span>
        <ChevronUp className="h-4 w-4" />
      </button>
    );
  }

  const body = (
    <div
      className={
        isRight
          ? 'overflow-y-auto flex-1 min-h-0 p-3 flex flex-col gap-y-4'
          : 'overflow-y-auto flex-1 min-h-0 p-3 flex flex-wrap gap-x-6 gap-y-4'
      }
    >
      {activeTool === 'marquee' ? (
        <MarqueePanel settings={marqueeSettings} onChange={onUpdateMarquee} />
      ) : activeTool === 'crop' ? (
        <CropPanel onApply={onApplyCrop} />
      ) : (
        <>
          {activeTool === 'draw' && (
            <BrushPanel settings={brushSettings} onChange={onUpdateBrush} />
          )}
          {activeTool === 'eraser' && (
            <BrushPanel settings={brushSettings} onChange={onUpdateBrush} eraserMode />
          )}
          {activeTool === 'text' && <TextPanel settings={textSettings} onChange={onUpdateText} />}
          {activeTool === 'shape' && (
            <ShapePanel settings={shapeSettings} onChange={onUpdateShape} />
          )}
          {activeTool === 'fill' && <FillPanel settings={brushSettings} onChange={onUpdateBrush} />}
          {activeTool === 'eyedropper' && (
            <EyedropperPanel
              color={colorUnderCursor}
              x={cursorX}
              y={cursorY}
              selectedColor={selectedColor}
              onColorPicked={onColorPicked}
            />
          )}
          {activeTool === 'move' && (
            <FilterPanel settings={filterSettings} onChange={onUpdateFilter} />
          )}
        </>
      )}
    </div>
  );

  const containerClass = isRight
    ? 'w-full bg-background border-l border-border flex flex-col flex-1 min-h-0'
    : 'w-full bg-background border-t border-border flex flex-col max-h-[50vh]';

  const HideIcon = isRight ? ChevronUp : ChevronDown;

  return (
    <div className={containerClass}>
      <div className="flex items-center justify-between px-3 h-10 border-b border-border shrink-0">
        <h3 className="text-sm font-medium">Properties</h3>
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
          onClick={onToggle}
        >
          Hide
          <HideIcon className="h-4 w-4" />
        </button>
      </div>
      {body}
    </div>
  );
}

const SECTION_CLASS = 'space-y-3 flex-1 min-w-[240px] max-w-sm';

function BrushPanel({
  settings,
  onChange,
  eraserMode,
}: {
  settings: BrushSettings;
  onChange: (s: Partial<BrushSettings>) => void;
  eraserMode?: boolean;
}) {
  return (
    <div className={SECTION_CLASS}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase">
        {eraserMode ? 'Eraser' : 'Brush'}
      </h4>
      {!eraserMode && (
        <label className="flex items-center gap-2">
          <span className="text-xs w-14">Color</span>
          <input
            type="color"
            value={settings.color}
            onChange={(e) => onChange({ color: e.target.value })}
            className="w-8 h-8 rounded border border-border cursor-pointer"
          />
        </label>
      )}
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Size</span>
        <input
          type="range"
          min={1}
          max={100}
          value={settings.width}
          onChange={(e) => onChange({ width: Number(e.target.value) })}
          className="flex-1"
        />
        <span className="text-xs w-8 text-right tabular-nums">{settings.width}</span>
      </label>
      {!eraserMode && (
        <label className="flex items-center gap-2">
          <span className="text-xs w-14">Opacity</span>
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(settings.opacity * 100)}
            onChange={(e) => onChange({ opacity: Number(e.target.value) / 100 })}
            className="flex-1"
          />
          <span className="text-xs w-8 text-right tabular-nums">
            {Math.round(settings.opacity * 100)}%
          </span>
        </label>
      )}
    </div>
  );
}

function FillPanel({
  settings,
  onChange,
}: {
  settings: BrushSettings;
  onChange: (s: Partial<BrushSettings>) => void;
}) {
  return (
    <div className={SECTION_CLASS}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase">Fill</h4>
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Color</span>
        <input
          type="color"
          value={settings.color}
          onChange={(e) => onChange({ color: e.target.value })}
          className="w-8 h-8 rounded border border-border cursor-pointer"
        />
      </label>
    </div>
  );
}

const WHEEL_SIZE = 140;

function EyedropperPanel({
  color,
  x,
  y,
  selectedColor,
  onColorPicked,
}: {
  color: string;
  x: number;
  y: number;
  selectedColor: string;
  onColorPicked: (color: string) => void;
}) {
  const safeColor = /^#[0-9a-fA-F]{6}$/.test(color) ? color : '#000000';
  const safeSelected = /^#[0-9a-fA-F]{6}$/.test(selectedColor) ? selectedColor : '#000000';
  const { r, g, b } = hexToRgb(safeColor);
  const hsv = rgbToHsv(r, g, b);
  const hsl = rgbToHsl(r, g, b);

  const radius = WHEEL_SIZE / 2;
  const angleRad = ((hsv.h - 90) * Math.PI) / 180;
  const dist = (hsv.s / 100) * radius;
  const markerX = radius + Math.cos(angleRad) * dist;
  const markerY = radius + Math.sin(angleRad) * dist;

  // Render the HSV wheel into a real <canvas> so click-to-pick can read the
  // exact pixel under the cursor (and the visual stays in sync with the
  // pick math). Hue = atan2 angle, saturation = distance/radius, value = 1.
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const size = c.width;
    const rad = size / 2;
    const img = ctx.createImageData(size, size);
    for (let py = 0; py < size; py++) {
      for (let px = 0; px < size; px++) {
        const dx = px - rad;
        const dy = py - rad;
        const d = Math.sqrt(dx * dx + dy * dy);
        const i = (py * size + px) * 4;
        if (d > rad) {
          img.data[i + 3] = 0;
          continue;
        }
        let angle = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
        if (angle < 0) angle += 360;
        const sat = Math.min(1, d / rad);
        const rgb = hsvToRgb(angle % 360, sat, 1);
        img.data[i] = rgb.r;
        img.data[i + 1] = rgb.g;
        img.data[i + 2] = rgb.b;
        img.data[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  }, []);

  const pickFromEvent = (clientX: number, clientY: number) => {
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const cx = ((clientX - rect.left) * c.width) / rect.width;
    const cy = ((clientY - rect.top) * c.height) / rect.height;
    const rad = c.width / 2;
    const dx = cx - rad;
    const dy = cy - rad;
    if (Math.sqrt(dx * dx + dy * dy) > rad) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const data = ctx.getImageData(Math.floor(cx), Math.floor(cy), 1, 1).data;
    if (data[3] === 0) return;
    onColorPicked(rgbToHex(data[0], data[1], data[2]));
  };

  return (
    <div className={SECTION_CLASS}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase">Eyedropper</h4>
      <div className="relative mx-auto" style={{ width: WHEEL_SIZE, height: WHEEL_SIZE }}>
        <canvas
          ref={canvasRef}
          width={WHEEL_SIZE}
          height={WHEEL_SIZE}
          className="rounded-full cursor-crosshair"
          onMouseDown={(e) => pickFromEvent(e.clientX, e.clientY)}
        />
        <div
          className="absolute rounded-full border-2 border-white pointer-events-none"
          style={{
            width: 14,
            height: 14,
            left: markerX,
            top: markerY,
            transform: 'translate(-50%, -50%)',
            backgroundColor: safeColor,
            boxShadow: '0 0 0 1px rgba(0,0,0,0.6)',
          }}
        />
      </div>
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded border border-border shrink-0"
          style={{ backgroundColor: safeColor }}
          aria-label="Current color preview"
        />
        <div className="text-xs flex-1 space-y-0.5 font-mono">
          <div>HEX&nbsp;&nbsp;{safeColor.toUpperCase()}</div>
          <div>
            RGB&nbsp;&nbsp;{r}, {g}, {b}
          </div>
          <div>
            HSL&nbsp;&nbsp;{hsl.h}°, {hsl.s}%, {hsl.l}%
          </div>
          <div className="text-muted-foreground">
            XY&nbsp;&nbsp;&nbsp;{x}, {y}
          </div>
        </div>
      </div>
      <div className="border-t border-border pt-2">
        <h5 className="text-xs font-medium text-muted-foreground uppercase mb-2">Selected Color</h5>
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded border border-border shrink-0"
            style={{ backgroundColor: safeSelected }}
            aria-label="Selected color"
          />
          <div className="text-xs flex-1 font-mono">{safeSelected.toUpperCase()}</div>
        </div>
      </div>
    </div>
  );
}

function TextPanel({
  settings,
  onChange,
}: {
  settings: TextSettings;
  onChange: (s: Partial<TextSettings>) => void;
}) {
  return (
    <div className={SECTION_CLASS}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase">Text</h4>
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Font</span>
        <select
          value={settings.fontFamily}
          onChange={(e) => onChange({ fontFamily: e.target.value })}
          className="flex-1 text-xs bg-background border border-border rounded px-2 py-1"
        >
          <option value="Arial">Arial</option>
          <option value="Helvetica">Helvetica</option>
          <option value="Georgia">Georgia</option>
          <option value="Times New Roman">Times New Roman</option>
          <option value="Courier New">Courier New</option>
          <option value="Verdana">Verdana</option>
        </select>
      </label>
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Size</span>
        <input
          type="number"
          min={8}
          max={200}
          value={settings.fontSize}
          onChange={(e) => onChange({ fontSize: Number(e.target.value) })}
          className="flex-1 text-xs bg-background border border-border rounded px-2 py-1"
        />
      </label>
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Color</span>
        <input
          type="color"
          value={settings.fill}
          onChange={(e) => onChange({ fill: e.target.value })}
          className="w-8 h-8 rounded border border-border cursor-pointer"
        />
      </label>
      <div className="flex gap-1">
        <button
          type="button"
          className={`px-2 py-1 text-xs rounded ${settings.fontWeight === 'bold' ? 'bg-primary text-primary-foreground' : 'bg-accent'}`}
          onClick={() =>
            onChange({ fontWeight: settings.fontWeight === 'bold' ? 'normal' : 'bold' })
          }
        >
          B
        </button>
        <button
          type="button"
          className={`px-2 py-1 text-xs rounded italic ${settings.fontStyle === 'italic' ? 'bg-primary text-primary-foreground' : 'bg-accent'}`}
          onClick={() =>
            onChange({ fontStyle: settings.fontStyle === 'italic' ? 'normal' : 'italic' })
          }
        >
          I
        </button>
        <button
          type="button"
          className={`px-2 py-1 text-xs rounded underline ${settings.underline ? 'bg-primary text-primary-foreground' : 'bg-accent'}`}
          onClick={() => onChange({ underline: !settings.underline })}
        >
          U
        </button>
      </div>
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Arch</span>
        <input
          type="range"
          min={-100}
          max={100}
          value={settings.arch}
          onChange={(e) => onChange({ arch: Number(e.target.value) })}
          className="flex-1"
        />
        <span className="text-xs w-8 text-right tabular-nums">{settings.arch}</span>
      </label>
    </div>
  );
}

function ShapePanel({
  settings,
  onChange,
}: {
  settings: ShapeSettings;
  onChange: (s: Partial<ShapeSettings>) => void;
}) {
  const shapes: { type: ShapeType; label: string }[] = [
    { type: 'rect', label: 'Rectangle' },
    { type: 'circle', label: 'Circle' },
    { type: 'triangle', label: 'Triangle' },
    { type: 'line', label: 'Line' },
  ];

  return (
    <div className={SECTION_CLASS}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase">Shape</h4>
      <div className="flex gap-1 flex-wrap">
        {shapes.map((s) => (
          <button
            key={s.type}
            type="button"
            className={`px-2 py-1 text-xs rounded ${settings.type === s.type ? 'bg-primary text-primary-foreground' : 'bg-accent'}`}
            onClick={() => onChange({ type: s.type })}
          >
            {s.label}
          </button>
        ))}
      </div>
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Fill</span>
        <input
          type="color"
          value={settings.fill === 'transparent' ? '#ffffff' : settings.fill}
          onChange={(e) => onChange({ fill: e.target.value })}
          className="w-8 h-8 rounded border border-border cursor-pointer"
        />
        <button
          type="button"
          className={`text-xs px-2 py-0.5 rounded ${settings.fill === 'transparent' ? 'bg-primary text-primary-foreground' : 'bg-accent'}`}
          onClick={() =>
            onChange({ fill: settings.fill === 'transparent' ? '#ffffff' : 'transparent' })
          }
        >
          {settings.fill === 'transparent' ? 'No fill' : 'Clear'}
        </button>
      </label>
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Stroke</span>
        <input
          type="color"
          value={settings.stroke}
          onChange={(e) => onChange({ stroke: e.target.value })}
          className="w-8 h-8 rounded border border-border cursor-pointer"
        />
      </label>
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Width</span>
        <input
          type="range"
          min={0}
          max={20}
          value={settings.strokeWidth}
          onChange={(e) => onChange({ strokeWidth: Number(e.target.value) })}
          className="flex-1"
        />
        <span className="text-xs w-8 text-right tabular-nums">{settings.strokeWidth}</span>
      </label>
    </div>
  );
}

// Inline SVGs for the rect/square/ellipse/circle marquee buttons. Lucide
// only ships `Square` and `Circle`; the wide-aspect Rectangle and Ellipse
// variants are drawn here so all four shape buttons stay visually consistent.
function RectangleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="6" width="20" height="12" rx="1" />
    </svg>
  );
}

function SquareIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="4" y="4" width="16" height="16" rx="1" />
    </svg>
  );
}

function EllipseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <ellipse cx="12" cy="12" rx="10" ry="6" />
    </svg>
  );
}

function CircleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}

function MarqueePanel({
  settings,
  onChange,
}: {
  settings: MarqueeSettings;
  onChange: (s: Partial<MarqueeSettings>) => void;
}) {
  // Rectangle/Square share `shape: 'rect'` and differ only by forceSquare.
  // Ellipse/Circle share `shape: 'ellipse'`. Lasso/Polygon/Wand don't care
  // about forceSquare, so we always reset it to false to keep state clean.
  const buttons: {
    id: string;
    label: string;
    shape: MarqueeShape;
    forceSquare: boolean;
    icon: React.ComponentType<{ className?: string }>;
  }[] = [
    {
      id: 'rect-rect',
      label: 'Rectangular Selection',
      shape: 'rect',
      forceSquare: false,
      icon: RectangleIcon,
    },
    {
      id: 'rect-square',
      label: 'Square Selection',
      shape: 'rect',
      forceSquare: true,
      icon: SquareIcon,
    },
    {
      id: 'ellipse-ellipse',
      label: 'Elliptical Selection',
      shape: 'ellipse',
      forceSquare: false,
      icon: EllipseIcon,
    },
    {
      id: 'ellipse-circle',
      label: 'Circular Selection',
      shape: 'ellipse',
      forceSquare: true,
      icon: CircleIcon,
    },
    { id: 'lasso', label: 'Free Selection', shape: 'lasso', forceSquare: false, icon: Lasso },
    {
      id: 'polygon',
      label: 'Path Selection',
      shape: 'polygon',
      forceSquare: false,
      icon: Pentagon,
    },
    { id: 'wand', label: 'Pixel Selection', shape: 'wand', forceSquare: false, icon: Wand2 },
  ];

  const isWand = settings.shape === 'wand';

  const activeButton = buttons.find((b) => {
    if (b.shape !== settings.shape) return false;
    if (b.shape === 'rect' || b.shape === 'ellipse') {
      return b.forceSquare === settings.forceSquare;
    }
    return true;
  });
  const headerLabel = activeButton?.label ?? 'Selection';

  return (
    <div className={SECTION_CLASS}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase">{headerLabel}</h4>
      <div className="flex gap-2 flex-wrap">
        {buttons.map((b) => {
          const Icon = b.icon;
          const isActive = activeButton?.id === b.id;
          return (
            <button
              key={b.id}
              type="button"
              title={b.label}
              className={`w-10 h-10 flex items-center justify-center rounded-md transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-accent text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => onChange({ shape: b.shape, forceSquare: b.forceSquare })}
            >
              <Icon className="h-5 w-5" />
            </button>
          );
        })}
      </div>
      {isWand && (
        <>
          <label className="flex items-center gap-2">
            <span className="text-xs w-24">Tolerance</span>
            <input
              type="range"
              min={0}
              max={255}
              value={settings.tolerance}
              onChange={(e) => onChange({ tolerance: Number(e.target.value) })}
              className="flex-1"
            />
            <span className="text-xs w-10 text-right tabular-nums">{settings.tolerance}</span>
          </label>
          <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
            <input
              type="checkbox"
              checked={settings.contiguous}
              onChange={(e) => onChange({ contiguous: e.target.checked })}
              className="w-4 h-4 cursor-pointer"
            />
            <span>Contiguous</span>
          </label>
        </>
      )}
      <label className="flex items-center gap-2">
        <span className="text-xs w-24">Marquee Color</span>
        <input
          type="color"
          value={settings.color}
          onChange={(e) => onChange({ color: e.target.value })}
          className="w-8 h-8 rounded border border-border cursor-pointer"
        />
      </label>
    </div>
  );
}

function CropPanel({ onApply }: { onApply: () => void }) {
  return (
    <div className={SECTION_CLASS}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase">Crop</h4>
      <p className="text-xs text-muted-foreground">
        Drag the corners or sides of the rectangle to set the crop area.
      </p>
      <button
        type="button"
        className="flex items-center gap-2 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
        onClick={onApply}
      >
        <Crop className="h-4 w-4" />
        Crop
      </button>
    </div>
  );
}

function FilterPanel({
  settings,
  onChange,
}: {
  settings: FilterSettings;
  onChange: (s: Partial<FilterSettings>) => void;
}) {
  const filters: { key: keyof FilterSettings; label: string }[] = [
    { key: 'brightness', label: 'Brightness' },
    { key: 'contrast', label: 'Contrast' },
    { key: 'saturation', label: 'Saturation' },
  ];

  return (
    <div className={SECTION_CLASS}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase">Filters</h4>
      {filters.map((f) => (
        <label key={f.key} className="flex items-center gap-2">
          <span className="text-xs w-16">{f.label}</span>
          <input
            type="range"
            min={-100}
            max={100}
            value={settings[f.key]}
            onChange={(e) => onChange({ [f.key]: Number(e.target.value) })}
            className="flex-1"
          />
          <span className="text-xs w-8 text-right tabular-nums">{settings[f.key]}</span>
        </label>
      ))}
      <button
        type="button"
        className="text-xs text-muted-foreground hover:text-foreground"
        onClick={() => onChange({ brightness: 0, contrast: 0, saturation: 0 })}
      >
        Reset filters
      </button>
    </div>
  );
}
